/* local-time.js — absolute timestamps in the viewer's DEVICE timezone.
 *
 * The app stores and renders every instant in UTC (config/app.php). That is
 * right for storage and wrong for display: a Moscow user who sends a message
 * at 19:35 saw it stamped 16:35, and said so ("на 3 часа отстаёт", 2026-08-03).
 *
 * The contract, mirrored by <x-local-time>:
 *
 *     <time datetime="2026-08-03T16:35:30+00:00" data-localtime="time">19:35</time>
 *            ^ the instant, always UTC          ^ preset        ^ text we rewrite
 *
 * Only the visible text is touched. `datetime` stays UTC because that is what
 * crawlers, screen readers and copy-paste consumers read — an ambiguous local
 * string there would be a real regression.
 *
 * Why the device and not the profile: the profile zone is a guess that goes
 * stale the moment someone travels, it is absent for guests (the forum and
 * /whats-new are public), and cached HTML is shared by viewers in different
 * zones. The device knows. The server-rendered text is only the fallback for
 * no-JS / no-Intl.
 *
 * Loaded site-wide, next to linkify-user-text.js, so the light admin layout
 * gets it too without pulling the 274 KB core bundle.
 */
(function () {
    'use strict';

    /* Presets are named, never inlined at call sites: a chat bubble and a forum
       post must not drift into two different clock formats. */
    var PRESETS = {
        time:      { hour: '2-digit', minute: '2-digit' },
        datetime:  { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
        daytime:   { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
        /* Weekday + clock — a bookable slot is chosen by "which day", so the
           day name earns its space here and nowhere else. */
        slot:      { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
        date:      { day: 'numeric', month: 'long', year: 'numeric' },
        dateshort: { day: 'numeric', month: 'short', year: 'numeric' },
        /* Tooltip form — a bare "19:35" loses the day, so the title carries it. */
        full:      { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    };

    /* Arabic Intl defaults to Arabic-Indic digits (٣), while the rest of the
       site prints Latin ones — pin the numbering system so a date does not
       become the only ٣ on the page. */
    function intlLocale() {
        var loc = (window.__LOCALE || document.documentElement.lang || 'en');
        return loc.indexOf('ar') === 0 ? 'ar-u-nu-latn' : loc;
    }

    var cache = {};
    function fmt(preset) {
        if (cache[preset]) return cache[preset];
        try {
            cache[preset] = new Intl.DateTimeFormat(intlLocale(), PRESETS[preset] || PRESETS.datetime);
        } catch (e) {
            cache[preset] = null;   // keep the server fallback
        }
        return cache[preset];
    }

    function parse(el) {
        var raw = el.getAttribute('datetime') || el.getAttribute('data-localtime-at');
        if (!raw) return null;
        var d = new Date(raw);
        return isNaN(d.getTime()) ? null : d;
    }

    /**
     * Rewrite every [data-localtime] under `root` (root itself included).
     * Idempotent: the source of truth is the datetime attribute, never the text
     * we previously wrote, so running it twice is a no-op.
     */
    function apply(root) {
        var scope = root || document;
        var sel = '[data-localtime],[data-localtime-title]';
        var nodes = scope.querySelectorAll ? scope.querySelectorAll(sel) : [];
        var all = [];
        if (scope.nodeType === 1 && scope.matches && scope.matches(sel)) all.push(scope);
        for (var i = 0; i < nodes.length; i++) all.push(nodes[i]);

        for (var j = 0; j < all.length; j++) {
            var el = all[j];
            /* Relative text ("3 часа назад") is already zone-proof, but the
               tooltip beside it is absolute and was not. Title-only mode keeps
               the text and fixes just that — the GitHub pattern. */
            if (!el.hasAttribute('data-localtime')) {
                var dt = parse(el), tf = fmt('full');
                if (dt && tf) el.setAttribute('title', tf.format(dt));
                continue;
            }
            var d = parse(el);
            if (!d) continue;
            var f = fmt(el.getAttribute('data-localtime') || 'datetime');
            if (!f) continue;
            el.textContent = f.format(d);
            /* The tooltip carries the full date, the way every chat client does.
               Skipped where the markup already put an explanatory title there. */
            if (!el.hasAttribute('data-localtime-notitle')) {
                var full = fmt('full');
                if (full) el.setAttribute('title', full.format(d));
            }
        }
    }

    /** Local YYYY-MM-DD — the grouping key for day dividers, in the device zone. */
    function dayKey(input) {
        var d = input instanceof Date ? input : new Date(input);
        if (isNaN(d.getTime())) return '';
        var p = function (n) { return (n < 10 ? '0' : '') + n; };
        return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
    }

    /**
     * "Today" / "Yesterday" / an absolute date — decided in the DEVICE zone.
     * Computing this server-side in UTC is what put a 00:30 message under
     * "yesterday" for every user east of Greenwich.
     */
    function dayLabel(input, labels) {
        var d = input instanceof Date ? input : new Date(input);
        if (isNaN(d.getTime())) return '';
        var f = fmt('date');
        var abs = f ? f.format(d) : '';
        var k = dayKey(d), now = new Date();
        // Fall back to the absolute date rather than an empty caption: a blank
        // divider bar is a worse bug than a missing "Today".
        if (k === dayKey(now)) return (labels && labels.today) || abs;
        var y = new Date(now.getTime()); y.setDate(y.getDate() - 1);
        if (k === dayKey(y)) return (labels && labels.yesterday) || abs;
        return abs;
    }

    window.__localTime = { apply: apply, dayKey: dayKey, dayLabel: dayLabel, format: function (v, p) {
        var d = v instanceof Date ? v : new Date(v);
        var f = fmt(p || 'datetime');
        return (f && !isNaN(d.getTime())) ? f.format(d) : '';
    } };

    /* Run immediately (the tag sits near the end of body, so the page is already
       parsed) and again on DOMContentLoaded for anything printed after it. */
    apply(document);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { apply(document); });
    }
})();
